/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import {
    useBlockProps,
    InspectorControls,
    MediaUpload,
    MediaUploadCheck,
} from '@wordpress/block-editor';
import {
    PanelBody,
    SelectControl,
    ToggleControl,
    RangeControl,
    Button,
    TextControl,
    BaseControl,
    ButtonGroup,
    Modal,
} from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';

/**
 * Internal dependencies
 */
const ALLOWED_MEDIA_TYPES = ['image'];

/**
 * Get proper text color class for arrows
 */
const getArrowTextColorClass = (arrowColor) => {
    const colorMap = {
        'red': 'text-primary',
        'blue': 'text-secondary',
        'white': 'text-white',
        'black': 'text-black',
    };
    return colorMap[arrowColor] || 'text-white';
};

/**
 * Edit component for the Image Slider block
 */
export default function Edit({ attributes, setAttributes }) {
    const {
        images: rawImages = [],
        layout = 'carousel',
        slidesToShow = 3,
        aspectRatio = '16/9',
        objectFit = 'cover',
        showArrows = true,
        showDots = true,
        showCaptions = true,
        autoplay = false,
        autoplaySpeed = 5000,
        enableLightbox = false,
        arrowStyle = 'default',
        arrowColor = 'white',
        useSubtitles = false,
        gridColumns = 3,
        rounded = 'none',
        enableLoadMore = false,
        loadMoreBatchSize = 6,
        initialItemsCount = 6,
    } = attributes;

    // Ensure all images have blockId for stable React keys
    const ensureImageBlockIds = (imageList) => {
        return imageList.map((image, index) => {
            if (!image.blockId) {
                return {
                    ...image,
                    blockId: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
                };
            }
            return image;
        });
    };

    const images = ensureImageBlockIds(rawImages);

    // Update attributes if images were processed to add blockIds
    useEffect(() => {
        if (rawImages.length > 0 && images.some((img, index) => !rawImages[index]?.blockId)) {
            setAttributes({ images });
        }
    }, [rawImages.length]);

    const blockProps = useBlockProps({
        className: 'wp-block-puroclean-image-slider',
    });

    // State for deletion confirmation
    const [isDeleting, setIsDeleting] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);

    const onSelectImages = (newImages) => {
        // Start with existing images
        const updatedImages = [...images];
        
        // Add all selected images as new entries (allowing duplicates)
        newImages.forEach((newImage) => {
            const formattedImage = {
                id: newImage.id,
                url: newImage.url,
                alt: newImage.alt || '',
                caption: newImage.caption || '',
                title: newImage.title || '',
                // Add a unique block-specific ID to prevent React key conflicts
                blockId: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            };
            
            // Always add as new image, even if it's a duplicate
            updatedImages.push(formattedImage);
        });
        
        setAttributes({ images: updatedImages });
    };

    const updateImageAtIndex = (index, key, value) => {
        const newImages = [...images];
        newImages[index] = {
            ...newImages[index],
            [key]: value,
        };
        setAttributes({ images: newImages });
    };

    const confirmDeleteImage = (index) => {
        if (index < 0 || index >= images.length) {
            console.warn('Invalid image index for removal:', index);
            return;
        }
        
        setImageToDelete(index);
        setIsDeleting(true);
    };

    const removeImageAtIndex = (index) => {
        // Validate index before removal
        if (index < 0 || index >= images.length) {
            console.warn('Invalid image index for removal:', index);
            return;
        }
        
        // Create a new array without the item at the specified index
        const newImages = images.filter((_, i) => i !== index);
        
        // Force a re-render by updating the attributes
        setAttributes({ images: newImages });
        
        // Close confirmation dialog
        setIsDeleting(false);
        setImageToDelete(null);
        
        // Log for debugging
        console.log(`Removed image at index ${index}. Remaining images: ${newImages.length}`);
    };

    const cancelDelete = () => {
        setIsDeleting(false);
        setImageToDelete(null);
    };

    const moveImageUp = (index) => {
        if (index === 0) return;
        const newImages = [...images];
        [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
        setAttributes({ images: newImages });
    };

    const moveImageDown = (index) => {
        if (index === images.length - 1) return;
        const newImages = [...images];
        [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
        setAttributes({ images: newImages });
    };

    // Helper function to get object-fit class
    const getObjectFitClass = () => {
        switch (objectFit) {
            case 'contain':
                return 'object-contain';
            case 'cover':
                return 'object-cover';
            case 'fill':
                return 'object-fill';
            case 'scale-down':
                return 'object-scale-down';
            case 'none':
                return 'object-none';
            default:
                return 'object-cover';
        }
    };

    // Helper function to get rounded class
    const getRoundedClass = () => {
        switch (rounded) {
            case 's':
                return 'rounded';
            case 'm':
                return 'rounded-md';
            case 'l':
                return 'rounded-lg';
            case 'xl':
                return 'rounded-xl';
            case '2xl':
                return 'rounded-2xl';
            case 'none':
            default:
                return '';
        }
    };

    // Get proper arrow text color class
    const arrowTextColorClass = getArrowTextColorClass(arrowColor);

    // Calculate how many images to show in preview for grid with load more
    const getPreviewImageCount = () => {
        if (layout === 'grid' && enableLoadMore) {
            return Math.min(initialItemsCount, images.length);
        }
        return layout === 'grid' ? Math.min(6, images.length) : 
               layout === 'slides' ? slidesToShow : 3;
    };

    return (
        <>
            <InspectorControls>
                <PanelBody title={__('Layout Settings', 'puroclean')} initialOpen={true}>
                    <SelectControl
                        label={__('Layout Type', 'puroclean')}
                        value={layout}
                        options={[
                            { label: __('Carousel', 'puroclean'), value: 'carousel' },
                            { label: __('Grid', 'puroclean'), value: 'grid' },
                            { label: __('Slides', 'puroclean'), value: 'slides' },
                        ]}
                        onChange={(value) => setAttributes({ layout: value })}
                    />

                    {(layout === 'slides') && (
                        <RangeControl
                            label={__('Images Per Slide', 'puroclean')}
                            value={slidesToShow}
                            onChange={(value) => setAttributes({ slidesToShow: value })}
                            min={1}
                            max={5}
                        />
                    )}

                    {(layout === 'grid') && (
                        <>
                            <RangeControl
                                label={__('Number of Columns', 'puroclean')}
                                value={gridColumns}
                                onChange={(value) => setAttributes({ gridColumns: value })}
                                min={1}
                                max={6}
                                help={__('Number of columns to display in grid layout', 'puroclean')}
                            />

                            <ToggleControl
                                label={__('Enable Load More', 'puroclean')}
                                checked={enableLoadMore}
                                onChange={(value) => setAttributes({ enableLoadMore: value })}
                                help={__('Show a "Load More" button to gradually reveal more images', 'puroclean')}
                            />

                            {enableLoadMore && (
                                <>
                                    <RangeControl
                                        label={__('Initial Items to Show', 'puroclean')}
                                        value={initialItemsCount}
                                        onChange={(value) => setAttributes({ initialItemsCount: value })}
                                        min={1}
                                        max={20}
                                        help={__('Number of images to show initially', 'puroclean')}
                                    />

                                    <RangeControl
                                        label={__('Load More Batch Size', 'puroclean')}
                                        value={loadMoreBatchSize}
                                        onChange={(value) => setAttributes({ loadMoreBatchSize: value })}
                                        min={1}
                                        max={20}
                                        help={__('Number of additional images to load each time "Load More" is clicked', 'puroclean')}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <SelectControl
                        label={__('Aspect Ratio', 'puroclean')}
                        value={aspectRatio}
                        options={[
                            { label: '16:9', value: '16/9' },
                            { label: '4:3', value: '4/3' },
                            { label: '1:1 (Square)', value: '1/1' },
                            { label: 'Auto', value: 'auto' },
                        ]}
                        onChange={(value) => setAttributes({ aspectRatio: value })}
                    />

                    <SelectControl
                        label={__('Image Fit', 'puroclean')}
                        value={objectFit}
                        options={[
                            { label: __('Cover (Fill & Crop)', 'puroclean'), value: 'cover' },
                            { label: __('Contain (Fit Inside)', 'puroclean'), value: 'contain' },
                            { label: __('Fill (Stretch)', 'puroclean'), value: 'fill' },
                            { label: __('Scale Down', 'puroclean'), value: 'scale-down' },
                            { label: __('None (Original)', 'puroclean'), value: 'none' },
                        ]}
                        onChange={(value) => setAttributes({ objectFit: value })}
                        help={__('How images should fit within their containers', 'puroclean')}
                    />
                </PanelBody>

                <PanelBody title={__('Navigation Settings', 'puroclean')} initialOpen={false}>
                    <ToggleControl
                        label={__('Show Navigation Arrows', 'puroclean')}
                        checked={showArrows}
                        onChange={(value) => setAttributes({ showArrows: value })}
                    />

                    {showArrows && (
                        <>
                            <BaseControl label={__('Arrow Style', 'puroclean')}>
                                <ButtonGroup>
                                    <Button
                                        isPrimary={arrowStyle === 'default'}
                                        onClick={() => setAttributes({ arrowStyle: 'default' })}
                                    >
                                        {__('Default', 'puroclean')}
                                    </Button>
                                    <Button
                                        isPrimary={arrowStyle === 'rounded'}
                                        onClick={() => setAttributes({ arrowStyle: 'rounded' })}
                                    >
                                        {__('Rounded', 'puroclean')}
                                    </Button>
                                    <Button
                                        isPrimary={arrowStyle === 'square'}
                                        onClick={() => setAttributes({ arrowStyle: 'square' })}
                                    >
                                        {__('Square', 'puroclean')}
                                    </Button>
                                    <Button
                                        isPrimary={arrowStyle === 'minimal'}
                                        onClick={() => setAttributes({ arrowStyle: 'minimal' })}
                                    >
                                        {__('Minimal', 'puroclean')}
                                    </Button>
                                </ButtonGroup>
                            </BaseControl>

                            <SelectControl
                                label={__('Arrow Color', 'puroclean')}
                                value={arrowColor}
                                options={[
                                    { label: __('Red', 'puroclean'), value: 'primary' },
                                    { label: __('White', 'puroclean'), value: 'white' },
                                    { label: __('Black', 'puroclean'), value: 'black' },
                                    { label: __('Blue', 'puroclean'), value: 'secondary' },
                                ]}
                                onChange={(value) => setAttributes({ arrowColor: value })}
                            />
                        </>
                    )}

                    <ToggleControl
                        label={__('Show Dots Navigation', 'puroclean')}
                        checked={showDots}
                        onChange={(value) => setAttributes({ showDots: value })}
                    />
                </PanelBody>

                <PanelBody title={__('Display Options', 'puroclean')} initialOpen={false}>
                    <ToggleControl
                        label={__('Show Image Captions', 'puroclean')}
                        checked={showCaptions}
                        onChange={(value) => setAttributes({ showCaptions: value })}
                    />

                    {showCaptions && (
                        <ToggleControl
                            label={__('Show Caption as Subtitle', 'puroclean')}
                            checked={useSubtitles}
                            onChange={(value) => setAttributes({ useSubtitles: value })}
                            help={__('Show captions below images instead of overlaid on them', 'puroclean')}
                        />
                    )}

                    <ToggleControl
                        label={__('Enable Lightbox', 'puroclean')}
                        checked={enableLightbox}
                        onChange={(value) => setAttributes({ enableLightbox: value })}
                    />

                    <SelectControl
                        label={__('Border Radius', 'puroclean')}
                        value={rounded}
                        options={[
                            { label: __('None', 'puroclean'), value: 'none' },
                            { label: __('Small (0.25rem)', 'puroclean'), value: 's' },
                            { label: __('Medium (0.375rem)', 'puroclean'), value: 'm' },
                            { label: __('Large (0.5rem)', 'puroclean'), value: 'l' },
                            { label: __('Extra Large (0.75rem)', 'puroclean'), value: 'xl' },
                            { label: __('2X Large (1rem)', 'puroclean'), value: '2xl' },
                        ]}
                        onChange={(value) => setAttributes({ rounded: value })}
                        help={__('Add rounded corners to the images in the slider', 'puroclean')}
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                <div className="image-slider-editor">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">
                            {__('Image Slider', 'puroclean')}
                        </h3>
                        <div className="text-sm text-gray-600">
                            {images.length} {images.length === 1 ? __('image', 'puroclean') : __('images', 'puroclean')}
                        </div>
                    </div>

                    {/* Image Upload and Management */}
                    <div className="mb-6">
                        <MediaUploadCheck>
                            <MediaUpload
                                onSelect={onSelectImages}
                                allowedTypes={ALLOWED_MEDIA_TYPES}
                                multiple={true}
                                value={images.map(img => img.id)}
                                render={({ open }) => (
                                    <Button 
                                        onClick={open} 
                                        variant="primary"
                                        className="mb-4"
                                    >
                                        {images.length === 0 
                                            ? __('Add Images', 'puroclean') 
                                            : __('Add More Images', 'puroclean')
                                        }
                                    </Button>
                                )}
                            />
                        </MediaUploadCheck>

                        {/* Individual Image Management */}
                        {images.length > 0 && (
                            <div className="space-y-4">
                                {images.map((image, index) => (
                                    <div key={image.blockId || `img_${image.id}_${index}`} className="border border-gray-300 rounded-lg p-4">
                                        <div className="flex gap-4">
                                            <div className="flex-shrink-0">
                                                <img 
                                                    src={image.url} 
                                                    alt={image.alt}
                                                    className={`w-20 h-20 ${getObjectFitClass()} ${getRoundedClass() || 'rounded'}`}
                                                />
                                            </div>
                                            <div className="flex-1 space-y-3">
                                                <TextControl
                                                    label={__('Alt Text', 'puroclean')}
                                                    value={image.alt}
                                                    onChange={(value) => updateImageAtIndex(index, 'alt', value)}
                                                    placeholder={__('Describe this image...', 'puroclean')}
                                                />
                                                <TextControl
                                                    label={__('Caption', 'puroclean')}
                                                    value={image.caption}
                                                    onChange={(value) => updateImageAtIndex(index, 'caption', value)}
                                                    placeholder={__('Image caption...', 'puroclean')}
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        onClick={() => moveImageUp(index)}
                                                        disabled={index === 0}
                                                        variant="secondary"
                                                        size="small"
                                                    >
                                                        {__('↑ Move Up', 'puroclean')}
                                                    </Button>
                                                    <Button
                                                        onClick={() => moveImageDown(index)}
                                                        disabled={index === images.length - 1}
                                                        variant="secondary"
                                                        size="small"
                                                    >
                                                        {__('↓ Move Down', 'puroclean')}
                                                    </Button>
                                                    <Button
                                                        onClick={() => confirmDeleteImage(index)}
                                                        variant="secondary"
                                                        isDestructive
                                                        size="small"
                                                    >
                                                        {__('Remove', 'puroclean')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    {images.length > 0 && (
                        <div className="border-t pt-6">
                            <h4 className="text-md font-medium mb-4">
                                {__('Preview', 'puroclean')} ({layout === 'carousel' ? __('Carousel', 'puroclean') : layout === 'grid' ? __('Grid', 'puroclean') : __('Slides', 'puroclean')})
                            </h4>
                            
                            {layout === 'grid' && (
                                <div className={`grid gap-4 ${
                                    gridColumns === 1 ? 'grid-cols-1' :
                                    gridColumns === 2 ? 'grid-cols-1 md:grid-cols-2' :
                                    gridColumns === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
                                    gridColumns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
                                    gridColumns === 5 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5' :
                                    gridColumns === 6 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6' :
                                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                }`}>
                                    {images.slice(0, getPreviewImageCount()).map((image, index) => (
                                        <div key={image.blockId || `preview_${image.id}_${index}`}>
                                            <div className="relative">
                                                <img 
                                                    src={image.url} 
                                                    alt={image.alt}
                                                    className={`w-full ${getObjectFitClass()} ${getRoundedClass()} ${
                                                        aspectRatio === '16/9' ? 'aspect-video' :
                                                        aspectRatio === '4/3' ? 'aspect-[4/3]' :
                                                        aspectRatio === '1/1' ? 'aspect-square' :
                                                        'h-48'
                                                    }`}
                                                />
                                                {showCaptions && image.caption && !useSubtitles && (
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-sm rounded-b">
                                                        {image.caption}
                                                    </div>
                                                )}
                                            </div>
                                            {showCaptions && image.caption && useSubtitles && (
                                                <div className="mt-2 text-center text-gray-700 text-sm font-medium">
                                                    {image.caption}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(layout === 'carousel' || layout === 'slides') && (
                                <div className="relative">
                                    <div className="flex gap-4 overflow-hidden">
                                        {images.slice(0, layout === 'slides' ? slidesToShow : 3).map((image, index) => (
                                            <div key={image.blockId || `carousel_${image.id}_${index}`} className={`${layout === 'slides' ? 'flex-1' : 'w-1/3'} flex-shrink-0`}>
                                                <div className="relative">
                                                    <img 
                                                        src={image.url} 
                                                        alt={image.alt}
                                                        className={`w-full ${getObjectFitClass()} ${getRoundedClass()} ${
                                                            aspectRatio === '16/9' ? 'aspect-video' :
                                                            aspectRatio === '4/3' ? 'aspect-[4/3]' :
                                                            aspectRatio === '1/1' ? 'aspect-square' :
                                                            'h-48'
                                                        }`}
                                                    />
                                                    {showCaptions && image.caption && !useSubtitles && (
                                                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-sm rounded-b">
                                                            {image.caption}
                                                        </div>
                                                    )}
                                                </div>
                                                {showCaptions && image.caption && useSubtitles && (
                                                    <div className="mt-2 text-center text-gray-700 text-sm font-medium">
                                                        {image.caption}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {showArrows && (
                                        <>
                                            <div className={`absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 ${
                                                arrowStyle === 'rounded' ? 'rounded-full' :
                                                arrowStyle === 'square' ? 'rounded-none' :
                                                arrowStyle === 'minimal' ? 'bg-transparent' :
                                                'rounded-md'
                                            } bg-black/50 flex items-center justify-center ${arrowTextColorClass}`}>
                                                ←
                                            </div>
                                            <div className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 ${
                                                arrowStyle === 'rounded' ? 'rounded-full' :
                                                arrowStyle === 'square' ? 'rounded-none' :
                                                arrowStyle === 'minimal' ? 'bg-transparent' :
                                                'rounded-md'
                                            } bg-black/50 flex items-center justify-center ${arrowTextColorClass}`}>
                                                →
                                            </div>
                                        </>
                                    )}
                                    
                                    {showDots && (
                                        <div className="flex justify-center mt-4 gap-2">
                                            {[...Array(Math.min(5, images.length))].map((_, index) => (
                                                <div key={index} className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {images.length > getPreviewImageCount() && (
                                <div className="text-center mt-4 text-gray-600 text-sm">
                                    {layout === 'grid' && enableLoadMore ? (
                                        <>
                                            <div className="mb-2">
                                                {__('Showing %d of %d images', 'puroclean')
                                                    .replace('%d', getPreviewImageCount())
                                                    .replace('%d', images.length)}
                                            </div>
                                            <div className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">
                                                {__('Load More', 'puroclean')} (+{Math.min(loadMoreBatchSize, images.length - getPreviewImageCount())})
                                            </div>
                                        </>
                                    ) : (
                                        __('+ %d more images', 'puroclean').replace('%d', 
                                            images.length - getPreviewImageCount()
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {images.length === 0 && (
                        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                            <div className="text-gray-400 mb-4">
                                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <p className="text-gray-600 mb-4">
                                {__('No images selected for this slider.', 'puroclean')}
                            </p>
                            <p className="text-sm text-gray-500">
                                {__('Click "Add Images" to get started.', 'puroclean')}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Deletion Confirmation Modal */}
            {isDeleting && (
                <Modal
                    title={__('Confirm Image Removal', 'puroclean')}
                    onRequestClose={cancelDelete}
                    className="image-slider-delete-modal"
                >
                    <div className="p-4">
                        <p className="mb-4">
                            {__('Are you sure you want to remove this image from the slider?', 'puroclean')}
                        </p>
                        {imageToDelete !== null && images[imageToDelete] && (
                            <div className="mb-4">
                                <img 
                                    src={images[imageToDelete].url} 
                                    alt={images[imageToDelete].alt}
                                    className="w-20 h-20 object-cover rounded"
                                />
                            </div>
                        )}
                        <div className="flex gap-2 justify-end">
                            <Button
                                onClick={cancelDelete}
                                variant="secondary"
                            >
                                {__('Cancel', 'puroclean')}
                            </Button>
                            <Button
                                onClick={() => removeImageAtIndex(imageToDelete)}
                                variant="primary"
                                isDestructive
                            >
                                {__('Remove Image', 'puroclean')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
} 